import React from 'react'

import { Stack } from '@chakra-ui/react'
import { useAtomValue, useSetAtom } from 'jotai'
import { usePermission } from 'src/features/auth/context'
import { Choice } from 'src/legacy_modules/entities/choice/entity'
import { MediumItem } from 'src/legacy_modules/entities/medium-item/entity'
import { SmallItem } from 'src/legacy_modules/entities/small-item/entity'
import { TemplateStatus } from 'src/legacy_modules/entities/template/entity'
import { PERMISSIONS } from 'src/lib/permission'
import styled from 'styled-components'

import { smallItemsAtomFamily, templateDetailAtom } from './atom'
import { SortOnlyCard } from './components/sort-only-card'
import { SortOnlyGroup } from './components/sort-only-group'
import { useEditCard } from './use-edit-card'

type Props = {
  index: number
  mediumItem: MediumItem
  releaseTried: boolean
  orderEditable: boolean
  templateStatus: TemplateStatus
  hideRolePermission: boolean
}

const Container = styled.div``

export const SortOnlyMediumItemGroup: React.FC<Props> = props => {
  const {
    index,
    mediumItem,
    releaseTried,
    orderEditable,
    templateStatus,
    hideRolePermission,
  } = props

  const { hasPermission } = usePermission()

  const v2 = true

  const setAtom = useSetAtom(templateDetailAtom)

  const onUpMediumItem = () => {
    setAtom({
      type: 'changeIndexMediumItem',
      index,
      direction: 'up',
    })
  }

  const onDownMediumItem = () => {
    setAtom({
      type: 'changeIndexMediumItem',
      index,
      direction: 'down',
    })
  }

  const onChangeMediumItem = (values: Partial<MediumItem>) => {
    setAtom({
      type: 'updateMediumItem',
      index,
      values,
    })
  }

  const onDeleteMediumItem = () => {
    setAtom({
      type: 'deleteMediumItem',
      index,
    })
  }

  const smallItems = useAtomValue(smallItemsAtomFamily(mediumItem.uid))

  const onChangeSmallItem = ({
    smallItemUid,
    values,
  }: {
    smallItemUid: string
    values: Partial<SmallItem>
  }) => {
    setAtom({
      type: 'updateSmallItem',
      mediumItemIndex: index,
      uid: smallItemUid,
      values,
    })
  }

  const onChangeSmallItemIndex = ({
    smallItemUid,
    direction,
  }: {
    smallItemUid: string
    direction: 'up' | 'down'
  }) => {
    setAtom({
      type: 'changeIndexSmallItem',
      mediumItemIndex: index,
      uid: smallItemUid,
      direction,
    })
  }

  const onRemoveSmallItem = ({ smallItemUid }: { smallItemUid: string }) => {
    setAtom({
      type: 'deleteSmallItem',
      mediumItemIndex: index,
      uid: smallItemUid,
    })
  }

  const onAddChoice = ({ smallItemUid }: { smallItemUid: string }) => {
    setAtom({ type: 'addChoice', mediumItemIndex: index, smallItemUid })
  }

  const onChangeChoice = ({
    smallItemUid,
    choiceIndex,
    values,
  }: {
    smallItemUid: string
    choiceIndex: number
    values: Partial<Choice>
  }) => {
    setAtom({
      type: 'updateChoice',
      mediumItemIndex: index,
      smallItemUid,
      choiceIndex,
      values,
    })
  }

  const onRemoveChoice = ({
    smallItemUid,
    choiceIndex,
  }: {
    smallItemUid: string
    choiceIndex: number
  }) => {
    setAtom({
      type: 'deleteChoice',
      mediumItemIndex: index,
      smallItemUid,
      choiceIndex,
    })
  }

  const onAddSmallItemToChoice = ({
    smallItemUid,
    choiceIndex,
  }: {
    smallItemUid: string
    choiceIndex: number
  }) => {
    setAtom({
      type: 'addSmallItemToChoice',
      mediumItemIndex: index,
      smallItemUid,
      choiceIndex,
      v2,
    })
  }

  const { canChangeSmallItemIndex } = useEditCard(index)

  return (
    <Container id={mediumItem.uid}>
      <Stack spacing="5">
        <SortOnlyGroup
          index={index}
          mediumItem={mediumItem}
          orderEditable={orderEditable}
          onUp={onUpMediumItem}
          onDown={onDownMediumItem}
          onChange={onChangeMediumItem}
          onDelete={onDeleteMediumItem}
          v2={v2}
        />

        <Stack spacing="5" pl="10">
          {smallItems.map(smallItem => (
            <SortOnlyCard
              key={smallItem.uid}
              smallItem={smallItem}
              smallItemMap={mediumItem.smallItemMap}
              orderEditable={orderEditable}
              hideRolePermission={hideRolePermission}
              canEditTemplate={hasPermission(PERMISSIONS.Template_SetHospitals)}
              onAddChoice={onAddChoice}
              onChangeSmallItem={onChangeSmallItem}
              onChangeSmallItemIndex={onChangeSmallItemIndex}
              onChangeChoice={onChangeChoice}
              onRemoveSmallItem={onRemoveSmallItem}
              onRemoveChoice={onRemoveChoice}
              onAddSmallItemToChoice={onAddSmallItemToChoice}
              marginBottom={12}
              releaseTried={releaseTried}
              templateStatus={templateStatus}
              v2={v2}
              canChangeSmallItemIndex={canChangeSmallItemIndex}
            />
          ))}
        </Stack>
      </Stack>
    </Container>
  )
}
